import { useCallback, useEffect, useState } from 'react';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { confirmationStatusFr, type ConfirmationMail } from '@lemontaigu/pms-core';
import { usePms } from './PmsProvider.js';

// « Confirmations à envoyer » (DESK-OPERATOR-AND-MAIL-BRIEF §3.1, M3): the
// confirmations prepared at « Enregistrer » and not sent yet — waiting for the
// card link, or failed — so none is forgotten. Each can be finished here: paste
// the link, send, or « Renvoyer ».

function PendingRow({ mail, onDone }: { mail: ConfirmationMail; onDone: () => void }) {
  const pms = usePms();
  const [link, setLink] = useState(mail.cardLink ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true);
    setError('');
    if (link.trim() !== (mail.cardLink ?? '')) {
      const l = await pms.confirmationMail.setLink(mail.id, link);
      if (!l.ok) {
        setBusy(false);
        setError(l.error.message);
        return;
      }
    }
    const r = await pms.confirmationMail.send(mail.id);
    setBusy(false);
    if (!r.ok) setError(r.error.message);
    else if (!r.value.sent && r.value.reason) setError(r.value.reason);
    onDone();
  };

  return (
    <li className="pms-pending-mail">
      <span>
        <strong>{mail.subject}</strong> · {mail.to} — {confirmationStatusFr(mail)}
      </span>
      {mail.status !== 'sending' && (
        <span className="pms-pending-mail-send">
          <input
            type="url"
            inputMode="url"
            placeholder="Lien carte (VAD) https://…"
            aria-label={`Lien carte pour ${mail.to}`}
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <button type="button" className="auth-btn" disabled={busy || !link.trim()} onClick={() => void send()}>
            {busy ? 'Envoi…' : mail.status === 'failed' ? 'Renvoyer' : 'Envoyer'}
          </button>
        </span>
      )}
      {error && <span className="auth-err">{error}</span>}
    </li>
  );
}

export function PendingConfirmations() {
  const pms = usePms();
  const writes = useCapability(CAPABILITIES.PMS_RESERVATION_WRITE);
  const [rows, setRows] = useState<ConfirmationMail[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await pms.confirmationMail.pending();
    if (r.ok) setRows(r.value);
  }, [pms]);

  // Only a login that writes bookings sends confirmations: nobody else reads the outbox.
  useEffect(() => {
    if (writes) void load();
  }, [load, writes]);

  if (!writes || rows.length === 0) return null;
  return (
    <div className="pms-pending-mails">
      <button type="button" className="pms-link" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {rows.length} confirmation{rows.length > 1 ? 's' : ''} à envoyer
      </button>
      {open && (
        <ul className="pms-sub">
          {rows.map((m) => (
            <PendingRow key={`${m.id}:${m.status}`} mail={m} onDone={() => void load()} />
          ))}
        </ul>
      )}
    </div>
  );
}
